import classNames from "classnames";
import type Flickity from "flickity";
import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import Modal from "react-modal";
import { t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import iconCarouselArrowLeft from "../../../img/icons/carousel-arrow-left.svg";
import iconCarouselArrowRight from "../../../img/icons/carousel-arrow-right.svg";
import iconGalleryArrowLeft from "../../../img/icons/gallery-arrow-left.svg";
import iconGalleryArrowRight from "../../../img/icons/gallery-arrow-right.svg";
import iconXClose from "../../../img/icons/x-close.svg";
import { safeDestroy } from "../../hooks/useFlickity";
import {
    detectTileVisibility,
    removeTabindex,
    setAriaHiddenAttribute,
} from "../../utils/detectFlickityTileVisibility";
import { focusElement } from "../../utils/keyboardFocus";
import { Image } from "../Image";

// Not using useFlickity hook: manages two Flickity instances with modal/resize interactions

Modal.defaultStyles = {
    overlay: {},
    content: {},
};

export type IGalleryImage = Immutable<{
    originalURL: string;
    originalAlt: string;
    thumbnailURL: string;
    thumbnailAlt: string;
    caption: string;
}>;

const SMALL_SCREEN_THRESHOLD = 501;
const MEDIUM_SCREEN_THRESHOLD = 769;

const getButtonAriaLabel = (
    slideNum: number,
    total: number,
    alt: string,
): string => {
    return t`Slide ${slideNum} of ${total}, ${alt}`;
};

const checkDisplayTilesNumber = (): number => {
    const window = getWindow();
    const innerWidth = window?.innerWidth ?? 0;
    const displayTilesNumber = !(innerWidth < MEDIUM_SCREEN_THRESHOLD)
        ? 5
        : !(innerWidth < SMALL_SCREEN_THRESHOLD)
          ? 4
          : 2;
    return displayTilesNumber;
};

const shouldInitFlickity = (numberOfSlides: number): boolean => {
    const window = getWindow();
    const innerWidth = window?.innerWidth ?? 0;
    const isSmallBreakpoint = innerWidth < SMALL_SCREEN_THRESHOLD;
    const isMediumBreakpoint = innerWidth < MEDIUM_SCREEN_THRESHOLD;
    return (
        (isSmallBreakpoint && numberOfSlides > 2) ||
        (isMediumBreakpoint && numberOfSlides > 4) ||
        numberOfSlides > 5
    );
};

const buildFullImages = (images: readonly IGalleryImage[]): React.ReactNode => {
    return images.map((img, i) => (
        <li
            key={i}
            className="gallery__item pdp-gallery-modal-item"
            data-src={img.originalURL}
            data-thumb={img.originalURL}
        >
            <div className="pdp-gallery-modal-item__container">
                <Image src={img.originalURL} alt={img.originalAlt} />
            </div>
            <div className="pdp-gallery-modal-item__info">
                <div
                    className="pdp-gallery-modal-item__caption"
                    dangerouslySetInnerHTML={{ __html: img.caption }}
                ></div>
            </div>
        </li>
    ));
};

type IProps = Immutable<{
    images: IGalleryImage[];
    alternateArrows?: boolean;
}>;

export const Gallery = (props: IProps): React.ReactNode => {
    const { images, alternateArrows = false } = props;

    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [selectedSlide, setSelectedSlide] = React.useState(1);
    const [selectedTilesGroupRange, setSelectedTilesGroupRange] =
        React.useState(0);
    const [selectedTileIndex, setSelectedTileIndex] = React.useState(0);
    const [thumbsBuilt, setThumbsBuilt] = React.useState(false);

    const selectedSlideRef = React.useRef(selectedSlide);

    const thumbCarouselRef = React.useRef<HTMLDivElement | null>(null);
    const imageCarouselRef = React.useRef<HTMLUListElement | null>(null);
    const thumbnailFlktyRef = React.useRef<Flickity | null>(null);
    const carouselFlktyRef = React.useRef<Flickity | null>(null);
    const flickityClassRef = React.useRef<typeof Flickity | null>(null);

    const initThumbnailFlickity = (carousel: HTMLDivElement) => {
        if (thumbnailFlktyRef.current || !flickityClassRef.current) {
            return;
        }

        const FlickityClass = flickityClassRef.current;
        thumbnailFlktyRef.current = new FlickityClass(carousel, {
            cellSelector: ".gallery__item",
            cellAlign: "left",
            contain: true,
            groupCells: true,
            pageDots: false,
            imagesLoaded: true,
            prevNextButtons: !alternateArrows,
        });

        thumbnailFlktyRef.current.on("select", () => {
            if (thumbnailFlktyRef.current) {
                setSelectedTilesGroupRange(
                    Number(thumbnailFlktyRef.current.selectedIndex) +
                        Number(checkDisplayTilesNumber() - 1),
                );
                setSelectedTileIndex(thumbnailFlktyRef.current.selectedIndex);
            }
        });
    };

    // Required: Stable reference needed for Modal library callback prop
    const onAfterOpenModal = React.useCallback(() => {
        const fullCarouselElem = imageCarouselRef.current;
        const FlickityClass = flickityClassRef.current;
        if (!fullCarouselElem || !FlickityClass) {
            return;
        }

        if (carouselFlktyRef.current) {
            safeDestroy(carouselFlktyRef.current);
        }

        carouselFlktyRef.current = new FlickityClass(fullCarouselElem, {
            cellAlign: "left",
            pageDots: false,
            prevNextButtons: false,
            imagesLoaded: true,
        });

        carouselFlktyRef.current.select(selectedSlideRef.current, true, true);
        // focus() exists on Flickity but not in @types/flickity
        (carouselFlktyRef.current as unknown as { focus: () => void }).focus();
        carouselFlktyRef.current.on("change", () => {
            if (carouselFlktyRef.current) {
                setSelectedSlide(carouselFlktyRef.current.selectedIndex);
            }
        });

        // ADA - Remove tabindex attribute which is added by flickity to avoid problems
        // for screen reader and keyboard users by disrupting the natural tab order
        const document = getDocument();
        const galleryModalContent = document?.querySelector(
            ".pdp-gallery-modal__content",
        );
        if (galleryModalContent) {
            galleryModalContent.removeAttribute("tabindex");
        }
        focusElement(".pdp-gallery-modal__close");
    }, []);

    // Required: Event listener needs stable reference for proper cleanup
    const recalcFlickityDisplay = React.useCallback(
        (thumbCarouselElem: HTMLDivElement) => {
            const numberOfSlides = images.length;
            if (shouldInitFlickity(numberOfSlides)) {
                initThumbnailFlickity(thumbCarouselElem);
            } else {
                if (thumbnailFlktyRef.current) {
                    safeDestroy(thumbnailFlktyRef.current);
                    thumbnailFlktyRef.current = null;
                }
            }
            onAfterOpenModal();
        },
        [images.length, onAfterOpenModal],
    );

    // componentDidMount equivalent - initialize Flickity and resize handler
    React.useEffect(() => {
        const thumbCarouselElem = thumbCarouselRef.current;
        if (!thumbCarouselElem) {
            return;
        }

        const window = getWindow();
        let resizeHandler: (() => void) | null = null;

        // Skip on SSR - flickity requires window
        if (import.meta.env.SSR) {
            return;
        }

        import("flickity").then((module) => {
            flickityClassRef.current = module.default;

            recalcFlickityDisplay(thumbCarouselElem);

            resizeHandler = () => {
                recalcFlickityDisplay(thumbCarouselElem);
            };

            window?.addEventListener("resize", resizeHandler);
        });

        // componentWillUnmount equivalent - cleanup
        return () => {
            if (resizeHandler) {
                window?.removeEventListener("resize", resizeHandler);
            }

            if (thumbnailFlktyRef.current) {
                safeDestroy(thumbnailFlktyRef.current);
                thumbnailFlktyRef.current = null;
            }
            if (carouselFlktyRef.current) {
                safeDestroy(carouselFlktyRef.current);
                carouselFlktyRef.current = null;
            }
        };
    }, [recalcFlickityDisplay]);

    // componentDidUpdate equivalent - update carousel and accessibility
    React.useEffect(() => {
        if (carouselFlktyRef.current) {
            carouselFlktyRef.current.select(selectedSlide);
        }

        selectedSlideRef.current = selectedSlide;

        setAriaHiddenAttribute("pdp-gallery-thumbs__item", "data-hidden");

        // ADA - Remove tabindex attribute which is added by flickity to avoid problems
        // for screen reader and keyboard users by disrupting the natural tab order
        removeTabindex("pdp-gallery-thumbs__list");
    }, [selectedSlide]);

    const onNavigationClick = (event: React.MouseEvent<HTMLElement>) => {
        const direction = event.currentTarget.dataset.direction;
        if (carouselFlktyRef.current) {
            switch (direction) {
                case "previous":
                    carouselFlktyRef.current.previous();
                    break;
                case "next":
                    carouselFlktyRef.current.next();
                    break;
                default:
                    break;
            }
        }
    };

    const openSelectedModalSlide = (
        slideIndex: number,
        event: React.MouseEvent<HTMLElement>,
    ) => {
        if (event) {
            event.preventDefault();
        }
        setModalIsOpen(true);
        setSelectedSlide(slideIndex);
    };

    const onCloseModal = (
        event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ) => {
        if (event) {
            event.preventDefault();
        }
        setModalIsOpen(false);
    };

    const onCarouselClick = (direction: string) => {
        if (thumbnailFlktyRef.current) {
            if (direction === "prev") {
                thumbnailFlktyRef.current.previous();
            } else if (direction === "next") {
                thumbnailFlktyRef.current.next();
            }
        }
    };

    const buildSlideDotButtons = (): React.ReactNode => {
        const dots = images.map((img, index) => {
            const classes = classNames({
                "is-selected": index === selectedSlide,
            });
            const slideNum = index + 1;
            return (
                <button
                    aria-current={index === selectedSlide}
                    aria-label={getButtonAriaLabel(
                        slideNum,
                        images.length,
                        img.originalAlt,
                    )}
                    className={classes}
                    key={`${index}`}
                    onClick={(event) => openSelectedModalSlide(index, event)}
                ></button>
            );
        });

        return <div className="pdp-gallery-modal__dots">{dots}</div>;
    };

    const buildThumbs = (): React.ReactNode => {
        const tilesNumber = images.length;
        const thumbImages = images.map((img, index) => {
            let isTileInViewport = true;
            if (thumbnailFlktyRef.current) {
                isTileInViewport = detectTileVisibility(
                    tilesNumber,
                    index,
                    selectedTilesGroupRange,
                    selectedTileIndex,
                );
            }

            const slideNum = index + 1;
            // Flickity adds its own classes to each cell, so the className
            // must never change after mount.
            return (
                <button
                    tabIndex={!isTileInViewport ? -1 : 0}
                    data-hidden={!isTileInViewport || undefined}
                    aria-label={getButtonAriaLabel(
                        slideNum,
                        images.length,
                        img.thumbnailAlt,
                    )}
                    className="gallery__item pdp-gallery-thumbs__item al-pdp-gallery__thumbnail"
                    key={index}
                    onClick={(event) => openSelectedModalSlide(index, event)}
                >
                    <Image src={img.thumbnailURL} alt={img.thumbnailAlt} />
                </button>
            );
        });

        // This thumbsBuilt flag ensures the custom next arrow element is placed
        // after the gallery thumb elements in the HTML
        // so that the custom arrow elements receive focus in a logical order for screen reader
        if (!thumbsBuilt) {
            setThumbsBuilt(true);
        }

        return thumbImages;
    };

    const classes = classNames({
        "gallery": true,
        "u-margin-bottom--xl": true,
        "l-capped-width": true,
    });

    const modalStyle = {
        overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
        content: {
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            margin: "auto",
            paddingTop: "35px",
            paddingBottom: "35px",
            overflow: "hidden",
            boxShadow: "none",
            zIndex: 99999,
        },
    };

    return (
        <div
            className={classes}
            role="region"
            aria-label={t`Product gallery carousel`}
        >
            <div>
                <div
                    className="pdp-gallery-thumbs__list"
                    ref={thumbCarouselRef}
                >
                    {alternateArrows && (
                        <button
                            className="pdp-gallery-thumbs__arrow-container pdp-gallery-thumbs__arrow-container--left"
                            aria-label={t`Previous`}
                            onClick={() => onCarouselClick("prev")}
                        >
                            <SVG
                                aria-hidden="true"
                                className="pdp-gallery-thumbs__arrow"
                                src={iconCarouselArrowLeft}
                            />
                        </button>
                    )}
                    {buildThumbs()}
                    {alternateArrows && thumbsBuilt && (
                        <button
                            className="pdp-gallery-thumbs__arrow-container pdp-gallery-thumbs__arrow-container--right"
                            aria-label={t`next`}
                            onClick={() => onCarouselClick("next")}
                        >
                            <SVG
                                aria-hidden="true"
                                className="pdp-gallery-thumbs__arrow"
                                src={iconCarouselArrowRight}
                            />
                        </button>
                    )}
                </div>
            </div>
            {!import.meta.env.SSR && (
                <Modal
                    aria={{
                        modal: true,
                        labelledby: "heading",
                    }}
                    className="pdp-gallery-modal"
                    isOpen={modalIsOpen}
                    onAfterOpen={onAfterOpenModal}
                    onRequestClose={onCloseModal}
                    role="dialog"
                    style={modalStyle}
                >
                    <h1 id="heading" className="ada-screenreader-only">
                        Gallery of product images
                    </h1>
                    <button
                        aria-label={t`Close`}
                        className="pdp-gallery-modal__close al-pdp-gallery-modal__close al-pdp-gallery__close"
                        onClick={onCloseModal}
                        type="button"
                    >
                        <SVG
                            aria-hidden="true"
                            className="pdp-gallery-modal__close-icon"
                            role="img"
                            src={iconXClose}
                            title={t`Close`}
                        />
                    </button>
                    <div
                        role="region"
                        aria-label={t`Gallery modal carousel`}
                        className="pdp-gallery-modal__content-container"
                    >
                        <div className="pdp-gallery-modal__buttons pdp-gallery-modal-item__container">
                            <button
                                aria-label={t`Previous`}
                                className="pdp-gallery-modal__button pdp-gallery-modal__button--previous al-pdp-gallery-modal__left"
                                data-direction="previous"
                                onClick={onNavigationClick}
                                type="button"
                            >
                                <SVG
                                    aria-hidden="true"
                                    className="pdp-gallery-modal__button-icon pdp-gallery-modal__button-icon--previous"
                                    src={iconGalleryArrowLeft}
                                    role="img"
                                    title={t`Arrow left`}
                                />
                            </button>
                        </div>
                        <ul
                            className="pdp-gallery-modal__content"
                            ref={imageCarouselRef}
                        >
                            {buildFullImages(images)}
                        </ul>
                        {buildSlideDotButtons()}
                        <div className="pdp-gallery-modal__buttons pdp-gallery-modal-item__container">
                            <button
                                aria-label={t`Next`}
                                className="pdp-gallery-modal__button pdp-gallery-modal__button--next al-pdp-gallery-modal__right"
                                data-direction="next"
                                onClick={onNavigationClick}
                                type="button"
                            >
                                <SVG
                                    aria-hidden="true"
                                    className="pdp-gallery-modal__button-icon pdp-gallery-modal__button-icon--next"
                                    src={iconGalleryArrowRight}
                                    role="img"
                                    title={t`Arrow right`}
                                />
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};
